import { Box, Card, CircularProgress, Grid, Icon, IconButton, InputLabel, Skeleton, Switch } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSelect from "components/MDSelect";
import MDTypography from "components/MDTypography";
import { Form, Formik, useFormikContext } from "formik";
import FormField from "layouts/pages/users/new-user/components/FormField";
import PropTypes from "prop-types";
import { memo, useEffect, useState } from 'react';
import { errorAlert, successAlert } from "utils/Functions";
import { get, postNoResponse } from "utils/WebService";
import * as Yup from "yup";

const trfOptions = [
    { value: 'trf1', label: 'TRF1' },
    { value: 'trf2', label: 'TRF2' },
    { value: 'trf3', label: 'TRF3' },
    { value: 'trf4', label: 'TRF4' },
    { value: 'trf5', label: 'TRF5' },
    { value: 'trf6', label: 'TRF6' },
];

const CredentialCreateEdit = ({ credentialId, closeCreateEdit, refreshTable }) => {
    const [isLoadingInfo, setIsLoadingInfo] = useState(false);
    const [sistemas, setSistemas] = useState([]);
    const [isLoadingSistemas, setIsLoadingSistemas] = useState(false);
    const [states, setStates] = useState([]);
    const [isLoadingEstados, setIsLoadingEstados] = useState(false);

    useEffect(() => {
        setIsLoadingEstados(true);
        get(`/addresses/states`).then(({ list }) => {
            setStates(list || []);
            setIsLoadingEstados(false);
        }).catch(() => {
            setStates([]);
            setIsLoadingEstados(false);
        });
    }, []);

    useEffect(() => {
        setIsLoadingSistemas(true);
        get(`/sistemas/list-all`).then((res) => {
            setSistemas(res.list || []);
            setIsLoadingSistemas(false);
        }).catch(() => {
            setSistemas([]);
            setIsLoadingSistemas(false);
        });
    }, []);

    const handleSubmit = (values, actions) => {
        const payload = {
            id: values.id,
            sistema: values.sistema,
            estado: values.estado,
            trf: values.trf ? values.trf.value : null,
            login: values.login,
            senha: values.senha,
            usaCertificado: values.usaCertificado,
            usaAutenticacao2F: values.usaAutenticacao2F
        };

        postNoResponse("/sistema-credencial/save", payload)
            .then(() => {
                successAlert(!credentialId ? "Credencial criada com sucesso!" : "Alterações salvas!", () => {
                    closeCreateEdit();
                    refreshTable();
                });
            })
            .catch(() => {
                errorAlert(!credentialId ? "Erro ao criar credencial!" : 'Erro ao salvar alterações.');
            })
            .finally(() => {
                actions.setSubmitting(false);
            });
    };

    return (
        <Card id="credential-info">
            <Box display="flex" alignItems="center" p={3}>
                <IconButton onClick={closeCreateEdit} sx={{ p: 0, paddingRight: 1, paddingTop: 0.75 }}>
                    <MDTypography variant="h5">
                        <Icon>arrow_back</Icon>
                    </MDTypography>
                </IconButton>
                <MDTypography variant="h6">{credentialId ? "Editar" : "Adicionar"} Credencial</MDTypography>
            </Box>
            <Formik
                initialValues={{
                    id: null,
                    sistema: null,
                    estado: null,
                    trf: null,
                    login: '',
                    senha: '',
                    usaCertificado: false,
                    usaAutenticacao2F: false,
                }}
                validationSchema={Yup.object().shape({
                    sistema: Yup.object().nullable().required("Selecione o sistema."),
                    estado: Yup.object().nullable(),
                    trf: Yup.object().nullable()
                }).test(
                    'estado-or-trf',
                    'Informe a UF ou o TRF.',
                    function(value) {
                        if (!value.estado && !value.trf) {
                            return this.createError({
                                path: 'estado',
                                message: 'Informe a UF ou o TRF.'
                            });
                        }
                        return true;
                    }
                )}
                onSubmit={(values, actions) => {
                    actions.setSubmitting(true);
                    handleSubmit(values, actions);
                }}>
                {({ values, errors, touched, isSubmitting, setFieldValue }) => {
                    return (
                        <Form autoComplete="off">
                            <FetchAndSetData credentialId={credentialId} setIsLoadingInfo={setIsLoadingInfo} closeCreateEdit={closeCreateEdit} />

                            <MDBox px={3}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Sistema/Tribunal</InputLabel>
                                        <MDSelect
                                            isLoading={isLoadingInfo || isLoadingSistemas}
                                            isMulti={false}
                                            getOptionValue={option => option.id}
                                            getOptionLabel={option => option.nome}
                                            name="sistema-select"
                                            placeholder="Selecione..."
                                            value={values.sistema}
                                            options={sistemas}
                                            onChange={selected => setFieldValue("sistema", selected)}
                                        />
                                        {errors.sistema && touched.sistema && (
                                            <MDTypography variant="caption" color="error">{errors.sistema}</MDTypography>
                                        )}
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>UF / Estado</InputLabel>
                                        <MDSelect
                                            isLoading={isLoadingInfo || isLoadingEstados}
                                            isMulti={false}
                                            getOptionValue={option => option.id}
                                            getOptionLabel={option => option.codEstado || option.descricao}
                                            name="estado-select"
                                            placeholder="Selecione..."
                                            value={values.estado}
                                            options={states}
                                            onChange={selected => setFieldValue("estado", selected)}
                                        />
                                        {errors.estado && touched.estado && (
                                            <MDTypography variant="caption" color="error">{errors.estado}</MDTypography>
                                        )}
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Região (TRF)</InputLabel>
                                        <MDSelect
                                            isLoading={isLoadingInfo}
                                            isMulti={false}
                                            getOptionValue={option => option.value}
                                            getOptionLabel={option => option.label}
                                            name="trf-select"
                                            placeholder="Selecione..."
                                            value={values.trf}
                                            options={trfOptions}
                                            onChange={selected => setFieldValue("trf", selected)}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <FormField
                                            loading={isLoadingInfo}
                                            type="text"
                                            label="Login"
                                            name="login"
                                            value={values.login}
                                            placeholder="Informe o login..."
                                            error={errors.login && touched.login}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormField
                                            loading={isLoadingInfo}
                                            type="password"
                                            label="Senha"
                                            name="senha"
                                            value={values.senha}
                                            placeholder="Informe a senha..."
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6} display="flex" alignItems="center">
                                        {isLoadingInfo ? <Skeleton width="50%" /> : (
                                            <>
                                                <Switch checked={values.usaCertificado || false} onChange={(e) => setFieldValue('usaCertificado', e.target.checked)} />
                                                <MDTypography variant="button" ml={1}>Usará Certificado Digital?</MDTypography>
                                            </>
                                        )}
                                    </Grid>
                                    <Grid item xs={12} sm={6} display="flex" alignItems="center">
                                        {isLoadingInfo ? <Skeleton width="50%" /> : (
                                            <>
                                                <Switch checked={values.usaAutenticacao2F || false} onChange={(e) => setFieldValue('usaAutenticacao2F', e.target.checked)} />
                                                <MDTypography variant="button" ml={1}>Usa Autenticação 2 Fatores?</MDTypography>
                                            </>
                                        )}
                                    </Grid>
                                </Grid>
                            </MDBox>

                            <Box display="flex" alignItems="center" justifyContent="end" p={2} mt={4}>
                                <MDButton
                                    disabled={isLoadingInfo || isLoadingSistemas || isLoadingEstados}
                                    size="large"
                                    variant="contained"
                                    color="info"
                                    type="submit"
                                    endIcon={isSubmitting ? <CircularProgress color="inherit" size={20} /> : null}>
                                    {!isSubmitting && (credentialId ? "Salvar" : "Adicionar")}
                                </MDButton>
                            </Box>
                        </Form>)
                }}
            </Formik>
        </Card>
    );
};

const FetchAndSetData = ({ credentialId, setIsLoadingInfo, closeCreateEdit }) => {
    const { setFieldValue } = useFormikContext();

    useEffect(() => {
        if (credentialId) {
            setIsLoadingInfo(true);
            get(`/sistema-credencial/by-id/${credentialId}`).then(data => {
                setFieldValue('id', data.id);
                setFieldValue('sistema', data.sistema);
                setFieldValue('estado', data.estado);
                setFieldValue('trf', data.trf ? trfOptions.find(t => t.value === data.trf) : null);
                setFieldValue('login', data.login);
                setFieldValue('senha', data.senha);
                setFieldValue('usaCertificado', data.usaCertificado);
                setFieldValue('usaAutenticacao2F', data.usaAutenticacao2F);
                setIsLoadingInfo(false);
            }).catch(() => errorAlert("Credencial não encontrada!", closeCreateEdit));
        }
    }, [credentialId]);

    return null;
};

CredentialCreateEdit.propTypes = {
    credentialId: PropTypes.string,
    closeCreateEdit: PropTypes.func.isRequired,
    refreshTable: PropTypes.func.isRequired,
};

export default memo(CredentialCreateEdit);
